import cx from 'classnames';
import { delay, identity, isNil } from 'lodash';
import { Component } from 'react';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSDropDownSearch, ELSTagGroup } from 'components/common/els';

interface SelectMultiselectDropdownProps {
  chartFormField: ChartMetaFormField;
  isDisabled?: boolean;
  ariaRequired?: boolean;
}

interface SelectMultiselectDropdownState {
  disableEmptyDropdown: boolean;
  options: ContentItem[];
  reset: number;
  search: string;
  selectedOptionNames: string[];
}

class SelectMultiselectDropdown extends Component<SelectMultiselectDropdownProps, SelectMultiselectDropdownState> {
  static displayName = 'SelectMultiselectDropdown';

  isUnmount = false;

  constructor(props: SelectMultiselectDropdownProps) {
    super(props);

    this.state = {
      disableEmptyDropdown: false,
      options: this.getFilteredOptions(''),
      reset: 0,
      search: '',
      selectedOptionNames: this.getSelectedOptionNames()
    };
  }

  componentDidUpdate(prevProps: SelectMultiselectDropdownProps) {
    const { chartFormField } = this.props;
    if (chartFormField !== prevProps.chartFormField) {
      if (chartFormField.chartContent) {
        this.setState((prevState) => {
          let { search, reset } = prevState;
          let options = this.getFilteredOptions(search);

          if (this.isChartContentChangedByVisibility(chartFormField.chartContent, prevProps.chartFormField.chartContent) || chartFormField.disabled) {
            reset += 1;
            search = '';
            options = this.getFilteredOptions(search);
          }

          return {
            disableEmptyDropdown: false,
            options,
            reset,
            search,
            selectedOptionNames: this.getSelectedOptionNames()
          };
        });
      } else {
        this.setState((prevState) => ({ disableEmptyDropdown: true, options: [], reset: prevState.reset + 1, search: '', selectedOptionNames: [] }));
      }
    }
  }

  componentWillUnmount() {
    this.isUnmount = true;
  }

  isChartContentChangedByVisibility = (chartContent: ContentItem[] = [], otherChartContent: ContentItem[] = []): boolean => {
    if (chartContent.length !== otherChartContent.length) {
      return true;
    }

    return !!chartContent.find((item) => !otherChartContent.find((otherItem) => item.value === otherItem.value && item.isHidden === otherItem.isHidden));
  };

  handleOptionSelect = (newSelectedOption: ContentItem) => {
    const { chartFormField } = this.props;
    const onChange = chartFormField.chartContent?.[0]?.onChange ?? identity;

    if (isNil(newSelectedOption.id ?? newSelectedOption.selected)) {
      delay(() => {
        if (!this.isUnmount) {
          this.setState({ search: '', options: this.getFilteredOptions('') });
        }
      }, 300);
    } else {
      onChange(chartFormField, newSelectedOption);
    }

    const { changeCallback } = chartFormField;
    if (changeCallback) {
      changeCallback({ formField: chartFormField, option: newSelectedOption });
    }
  };

  handleSearchChange = (value: string) => {
    this.setState({
      search: value,
      options: this.getFilteredOptions(value)
    });
  };

  handleTagDelete = (index: number) => {
    const { chartFormField } = this.props;
    const changedOption = chartFormField.chartContent?.filter((option) => option.selected && !option.isHidden)[index];
    if (!changedOption) return;
    changedOption.onChange(chartFormField, changedOption);
  };

  getSelectedOptionNames = (): string[] => {
    return this.props.chartFormField.chartContent?.filter((option) => option.selected && !option.isHidden).map((option) => option.name) ?? [];
  };

  getFilteredOptions = (search: string): ContentItem[] => {
    return this.props.chartFormField.chartContent?.filter((item) => !item.isHidden && item.name.toLowerCase().includes(search.toLowerCase())) ?? [];
  };

  render() {
    const { chartFormField, isDisabled, ariaRequired } = this.props;
    const { disabled, name, label } = chartFormField;
    const { disableEmptyDropdown, options, reset, selectedOptionNames } = this.state;
    const shouldDisable = isDisabled || disabled || disableEmptyDropdown;

    return (
      <div className={cx('sng-multi-select-search', name)}>
        <span className="u-els-hide-visually" aria-live="polite">{`${options.length} items in list`}</span>
        <ELSDropDownSearch
          className="sng-multi-select-search__dropdown"
          disableAutoComplete
          id={name}
          isDisabled={shouldDisable}
          key={reset}
          name={name}
          onOptionSelected={this.handleOptionSelect}
          onSearchChangeHandler={this.handleSearchChange}
          options={options}
          placeholder="Select"
          showSearchIcon={false}
          type="checkbox"
          ariaLabel={label}
          ariaRequired={ariaRequired}
        />
        {!!selectedOptionNames.length && (
          <div className="u-els-margin-top">
            <ELSTagGroup
              isLargerChipText
              isTagDeletable={!shouldDisable}
              cssModifier="sng-multi-select-search__tag-name-group"
              tags={selectedOptionNames}
              onDeleteTag={this.handleTagDelete}
            />
          </div>
        )}
      </div>
    );
  }
}

export default SelectMultiselectDropdown;
